import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMediaQuery, useTheme } from '@mui/material';
import { loginSchema, type LoginFormValues } from '../schemas/loginSchema';
import { ForgotPasswordFieldError } from '../types/forgotPassword';
import { useAuth } from './useAuth';

export function useLoginPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    loading,
    showActiveSessionModal,
    login,
    googleSignIn,
    forgotPassword,
    confirmActiveSessionLogin,
    dismissActiveSessionModal,
    dismissActiveSessionAndSignOut,
  } = useAuth();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const handleConfirmActiveSessionLogin = useCallback(() => {
    void confirmActiveSessionLogin(form.getValues());
  }, [confirmActiveSessionLogin, form]);

  const handleForgotPasswordClick = useCallback(async () => {
    try {
      await forgotPassword(form.getValues('email'));
    } catch (error) {
      if (error instanceof ForgotPasswordFieldError) {
        form.setError(error.field, { type: 'manual', message: error.message });
      }
    }
  }, [forgotPassword, form]);

  return {
    isMobile,
    form,
    loading,
    showActiveSessionModal,
    login,
    googleSignIn,
    dismissActiveSessionModal,
    dismissActiveSessionAndSignOut,
    handleConfirmActiveSessionLogin,
    handleForgotPasswordClick,
  };
}
